import { z } from "zod";
import { AccountType } from "../constants/domains/accounting/accounts";
import {
    ChartOfAccountsTemplateVariableKind,
    MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS,
    MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS,
} from "../constants/domains/accounting/chartOfAccountsTemplateAccount";
import { AccountRole } from "../types/domains/accounting/account";
import {
    ACCOUNT_CODE_TEXT_MAX_LENGTH,
    form1099BoxSchema,
} from "./domains/accounting/account.schema";

// Prefix marking a client-generated temp id (new, not-yet-persisted row), as
// opposed to a real uuid for an existing row. Shared with the backend service
// that resolves temp ids to uuids.
export const CLIENT_TEMP_ID_PREFIX = "temp:";

// Rows carry either a real uuid (existing row) or a "temp:"-prefixed client id
// (new row). Temp ids let the client reference a freshly created group from
// accounts/child groups before the server assigns the real uuid.
const ClientId = z
    .string()
    .min(1)
    .max(255)
    .refine(
        (value) =>
            value.startsWith(CLIENT_TEMP_ID_PREFIX) ||
            z.string().uuid().safeParse(value).success,
        { message: "id must be a uuid or a temp: client id" },
    );

// Upper bound of the Postgres `integer` (int4) column that backs codes, so a
// value above it is rejected at the schema layer instead of overflowing on
// insert. All these values are non-negative.
const PG_INT4_MAX = 2147483647;

// `sortOrder` is a fractional index persisted into a numeric(20,10) column, so it
// allows decimals (drag-drop writes the midpoint between two neighbours) and can
// dip below 0 when a row is dragged/inserted above the 0-based seed floor. The
// int4 range is reused as a generous, symmetric, overflow-safe bound: it is far
// inside numeric(20,10)'s 10-digit integer part, so no value that passes here
// can overflow the column.
const sortOrderSchema = z
    .number()
    .min(-PG_INT4_MAX)
    .max(PG_INT4_MAX)
    .optional();

export const TemplateAccountGroupInputSchema = z.object({
    id: ClientId,
    name: z.string().min(1).max(255),
    type: z.nativeEnum(AccountType),
    parentGroupId: ClientId.nullable(),
    sortOrder: sortOrderSchema,
});

export const TemplateAccountInputSchema = z.object({
    id: ClientId,
    codeText: z.string().trim().max(ACCOUNT_CODE_TEXT_MAX_LENGTH).nullable(),
    kickOriginalCode: z.number().int().min(0).max(PG_INT4_MAX).nullable(),
    name: z.string().min(1).max(255),
    type: z.nativeEnum(AccountType),
    isDisabled: z.boolean(),
    isManuallyCreated: z.boolean(),
    role: z.nativeEnum(AccountRole).nullable(),
    variableKind: z.nativeEnum(ChartOfAccountsTemplateVariableKind).nullable(),
    form1099Box: form1099BoxSchema,
    parentGroupId: ClientId.nullable(),
    sortOrder: sortOrderSchema,
});

// Upper bounds guard against an unbounded payload forcing unbounded per-row
// work inside the save transaction. Generous relative to a realistic template
// (a real chart of accounts is ~120-300 accounts) while keeping worst-case
// per-row reconciliation work bounded.
export const ChartOfAccountsTemplateAccountsBulkSaveSchema = z.object({
    groups: z
        .array(TemplateAccountGroupInputSchema)
        .max(MAX_CHART_OF_ACCOUNTS_TEMPLATE_GROUPS, {
            message: "Too many groups",
        }),
    accounts: z
        .array(TemplateAccountInputSchema)
        .max(MAX_CHART_OF_ACCOUNTS_TEMPLATE_ACCOUNTS, {
            message: "Too many accounts",
        }),
});

export type TemplateAccountGroupInput = z.infer<
    typeof TemplateAccountGroupInputSchema
>;

export type TemplateAccountInput = z.infer<typeof TemplateAccountInputSchema>;

export type ChartOfAccountsTemplateAccountsBulkSave = z.infer<
    typeof ChartOfAccountsTemplateAccountsBulkSaveSchema
>;
